/**
 * The organization — places, what is in them, and who can reach them.
 *
 * `docs/organization-build-book.md` §4. Its own file rather than more of
 * `api.ts`, because the org tree, its yards and zones and the grants over it
 * are one subject with a dozen shapes, and `api.ts` is already the place
 * every other subject ended up.
 */
import { z } from 'zod';
import { contrast } from './brand-palette.js';
import { ORG_UNIT_KINDS } from './enums.js';
import { permissionMatrix } from './api.js';
const uuid = z.string().uuid();
const isoDate = z.string().datetime();
/**
 * Why a place has no map position (Aaron, Oct 27 — the forecast that showed
 * nowhere): no address to look up, an address Google could not place, address
 * lookup not set up (Rowted Admin › Integrations › Maps), or Google refusing
 * or not answering. `services/org-positions.ts` decides; the Organization
 * card and Rowted Admin › Health say it.
 */
export const POSITION_PROBLEMS = ['no_address', 'not_found', 'lookup_off', 'lookup_failed'];
/** Two to six of `[A-Z0-9]` — what `org_units_code_shape` takes (0075). */
export const orgCodeSchema = z.string().trim().toUpperCase().regex(/^[A-Z0-9]{2,6}$/, 'A code is two to six letters or digits.');
/** Contact details a place puts on a document. Every field is optional. */
const contactFields = {
    addressLine1: z.string().trim().max(200).nullish(),
    addressLine2: z.string().trim().max(200).nullish(),
    city: z.string().trim().max(120).nullish(),
    state: z.string().trim().max(60).nullish(),
    postalCode: z.string().trim().max(20).nullish(),
    phone: z.string().trim().max(40).nullish(),
    email: z.string().trim().email().max(200).nullish().or(z.literal('')),
    licenseNumber: z.string().trim().max(60).nullish(),
};
export const orgUnitCounts = z.object({
    branches: z.number().int().nonnegative(),
    people: z.number().int().nonnegative(),
    crews: z.number().int().nonnegative(),
    yards: z.number().int().nonnegative(),
    zones: z.number().int().nonnegative(),
});
/**
 * A row of the tree.
 *
 * `reachable` is false for a unit drawn only so the tree makes sense — a
 * district manager sees the region above their district so the shape reads,
 * but cannot open it. `active` is false for a closed unit, which stays
 * visible here and disappears from every picker.
 */
export const orgUnitRow = z.object({
    id: uuid,
    kind: z.enum(ORG_UNIT_KINDS),
    name: z.string(),
    code: z.string(),
    parentId: uuid.nullable(),
    active: z.boolean(),
    reachable: z.boolean(),
    counts: orgUnitCounts,
});
export const yardView = z.object({
    id: uuid,
    name: z.string(),
    addressLine1: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postalCode: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    isDefault: z.boolean(),
    note: z.string(),
    /** The row's right-hand line, and what makes retiring one a 409. */
    itemsOnHand: z.number().int().nonnegative(),
    stockValueCents: z.number().int().nonnegative(),
    crews: z.number().int().nonnegative(),
});
export const zoneView = z.object({
    id: uuid,
    name: z.string(),
    colour: z.string(),
    active: z.boolean(),
    sortOrder: z.number().int(),
    /** How many properties are in it — what makes removing one a 409. */
    properties: z.number().int().nonnegative(),
    crews: z.number().int().nonnegative(),
});
export const orgUnitDetail = orgUnitRow.extend({
    timezone: z.string().nullable(),
    /**
     * The zone this place keeps from above when it has none of its own and its
     * address names none — the place it sits under, walked up to the company
     * (`org_unit_time_zone()`, migration 0137). What the selector's *Automatic*
     * says when there is no address to go by.
     */
    inheritedTimezone: z.string().nullable().optional(),
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postalCode: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    licenseNumber: z.string().nullable(),
    managerUserId: uuid.nullable(),
    managerName: z.string().nullable(),
    managerRole: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    /**
     * Why there is no map position, when there is none (Aaron, Oct 27): the
     * forecast and drive times start from it, so the card says what to fix.
     */
    positionProblem: z.enum(POSITION_PROBLEMS).nullable().optional(),
    closedAt: isoDate.nullable(),
    parentName: z.string().nullable(),
    yards: z.array(yardView),
    zones: z.array(zoneView),
    crews: z.array(z.object({ id: uuid, name: z.string() })),
    /** The card's footer line — who last changed it, and when. */
    updatedAt: isoDate.nullable(),
    updatedByName: z.string().nullable(),
    version: z.number().int(),
});
export const orgUnitWrite = z.object({
    name: z.string().trim().min(1, 'A name is required.').max(120),
    code: orgCodeSchema,
    timezone: z.string().trim().max(60).nullish(),
    managerUserId: uuid.nullish(),
    latitude: z.number().min(-90).max(90).nullish(),
    longitude: z.number().min(-180).max(180).nullish(),
    ...contactFields,
});
export const orgUnitCreate = z.object({
    name: z.string().trim().min(1, 'A name is required.').max(120),
    code: orgCodeSchema,
    /* Never 'company': there is one root and signup made it. */
    kind: z.enum(['region', 'district', 'branch']),
    parentId: uuid,
    /** A branch with nowhere to keep stock is a branch that cannot close out. */
    seedYard: z.boolean().default(true),
    /** Take the time zone and the zone list from an existing unit. */
    copyFromId: uuid.nullish(),
    /** Give the parent's manager their role here too. */
    grantParentManager: z.boolean().default(true),
});
export const orgUnitMove = z.object({ parentId: uuid });
const personRef = z.object({ id: uuid, name: z.string(), roleName: z.string() });
/**
 * What moving a unit would do to who can see what — shown *before* the move.
 *
 * Moving York from the Harrisburg district to the Lancaster district hands it
 * to one set of managers and takes it from another, and neither set is
 * looking at this screen. The preview is the only place that fact exists
 * before it is true.
 */
export const moveConsequence = z.object({
    comesAlong: z.number().int().nonnegative(),
    loses: z.array(personRef),
    gains: z.array(personRef),
});
export const yardWrite = z.object({
    name: z.string().trim().min(1, 'A name is required.').max(120),
    addressLine1: z.string().trim().max(200).nullish(),
    city: z.string().trim().max(120).nullish(),
    state: z.string().trim().max(60).nullish(),
    postalCode: z.string().trim().max(20).nullish(),
    latitude: z.number().min(-90).max(90).nullish(),
    longitude: z.number().min(-180).max(180).nullish(),
    isDefault: z.boolean().default(false),
    note: z.string().trim().max(500).default(''),
});
export const zoneWrite = z.object({
    name: z.string().trim().min(1, 'A name is required.').max(120),
    colour: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'A colour is a hex like #2e7d4f.'),
    active: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(999).default(0),
});
export const grantWrite = z.object({
    orgUnitId: uuid,
    roleId: uuid,
    note: z.string().trim().max(200).default(''),
});
/**
 * A grant, as a screen draws it.
 *
 * `inherited` marks one that comes from a unit above the one being looked at —
 * "from Harrisburg district" on the York access list — because the difference
 * between a grant you can remove here and one you cannot is the difference
 * between a working screen and a confusing one.
 */
export const grantView = z.object({
    id: uuid,
    orgUnitId: uuid,
    orgUnitName: z.string(),
    kind: z.enum(ORG_UNIT_KINDS),
    userId: uuid,
    userName: z.string(),
    roleId: uuid,
    roleName: z.string(),
    note: z.string(),
    grantedByName: z.string().nullable(),
    createdAt: isoDate,
    reaches: z.object({
        branches: z.number().int().nonnegative(),
        yards: z.number().int().nonnegative(),
        crews: z.number().int().nonnegative(),
        properties: z.number().int().nonnegative(),
    }),
    inherited: z.boolean().default(false),
    /** A weaker grant that a stronger one above it already covers. */
    outranked: z.boolean().default(false),
    permissions: permissionMatrix.optional(),
});
export const orgAccess = z.object({
    here: z.array(grantView),
    inherited: z.array(grantView),
    below: z.array(grantView),
});
/** Closing a unit with work still on it, and what to do about it. */
export const closeUnit = z.object({
    moveOpenVisitsToParent: z.boolean().default(false),
});
/**
 * A company's brand colour before it has chosen one.
 *
 * The same value `company_profile.brand_color` defaults to (0075), named
 * rather than repeated: it is stored data — it ends up on a proposal PDF and
 * in the portal's CSS — so it cannot be a token that changes with dark mode,
 * and the server, the client and the column must not be able to disagree.
 */
export const DEFAULT_BRAND_COLOR = '#2E7D4F';
/**
 * The colours a zone may be given.
 *
 * Hex, and hex on purpose: a zone's colour is *stored data* — it is written to
 * `zones.colour`, drawn on the schedule board, and sent to the crew app, so it
 * cannot be a CSS variable that means one thing in light mode and another in
 * dark. These are the same eight the demo world seeds, which are the plan §3.1
 * service-line and job-lifecycle colours picked for their contrast against the
 * board's white.
 */
export const ZONE_COLOURS = [
    { value: '#2e7d4f', label: 'Green' },
    { value: '#b0731a', label: 'Amber' },
    { value: '#2b66a8', label: 'Blue' },
    { value: '#1f8a8a', label: 'Teal' },
    { value: '#7a4fb0', label: 'Purple' },
    { value: '#b42318', label: 'Red' },
    { value: '#5b6478', label: 'Slate' },
    { value: '#5b6fb5', label: 'Indigo' },
];
// ---------------------------------------------------------------------------
// The company's own profile — `organization-build-book.md` §5.5 and §6.1
// ---------------------------------------------------------------------------
/**
 * Who the company is, as customers see it.
 *
 * One row per tenant (0075). Everything on a document, an email, the portal
 * and the crew app's accent comes from here, with a branch's own address and
 * phone in front of it when `docUseBranchContact` is on — which is what
 * `documentIdentity()` on the server resolves, field by field.
 */
export const companyProfile = z.object({
    displayName: z.string().trim().min(1, 'A name is required.').max(120),
    legalName: z.string().trim().max(160).default(''),
    tagline: z.string().trim().max(160).default(''),
    website: z.string().trim().max(200).default(''),
    addressLine1: z.string().trim().max(200).nullish(),
    addressLine2: z.string().trim().max(200).nullish(),
    city: z.string().trim().max(120).nullish(),
    state: z.string().trim().max(60).nullish(),
    postalCode: z.string().trim().max(20).nullish(),
    phone: z.string().trim().max(40).nullish(),
    email: z.string().trim().max(200).nullish(),
    licenseNumber: z.string().trim().max(60).nullish(),
    brandColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'A colour is a hex like #2e7d4f.'),
    docHeader: z.string().trim().max(200).default(''),
    docFooter: z.string().trim().max(300, 'Up to 300 characters.').default(''),
    docShowLicense: z.boolean().default(true),
    docUseBranchContact: z.boolean().default(true),
    docBrandBand: z.boolean().default(true),
    docLogoOnWorkOrder: z.boolean().default(false),
    paperSize: z.enum(['letter', 'a4']).default('letter'),
    emailFromName: z.string().trim().max(120).default(''),
    replyTo: z.string().trim().max(200).nullish(),
    smsSenderName: z.string().trim().max(11, 'Eleven characters at most.').default(''),
    dateFormat: z.string().trim().max(40).default('EEE MMM d, yyyy'),
    weekStart: z.union([z.literal(0), z.literal(1)]).default(1),
    units: z.enum(['imperial', 'metric']).default('imperial'),
});
/** What the read gives back on top of what a write takes. */
export const companyProfileView = companyProfile.extend({
    logoUrl: z.string().nullable(),
    logoDarkUrl: z.string().nullable(),
    markUrl: z.string().nullable(),
    currency: z.string(),
    timezone: z.string().nullable(),
    version: z.number().int(),
});
export const LOGO_SLOTS = ['light', 'dark', 'mark'];
/**
 * The eight brand colours offered as presets.
 *
 * Stored data, like `ZONE_COLOURS`: the chosen one is written to
 * `company_profile.brand_color` and rendered in an email, a PDF and the
 * portal, none of which has our CSS variables.
 */
export const BRAND_PRESETS = [
    '#2E7D4F', '#1F5C39', '#2B66A8', '#1F8A8A',
    '#7A4FB0', '#B0731A', '#B42318', '#1D1D1A',
];
/**
 * How legible a colour's own text is on white — WCAG's contrast ratio.
 *
 * The branding screen prints it beside the swatch because a brand colour is
 * not only decoration here: it is the colour a "View proposal" button's
 * *label* is drawn in on a customer's screen, and at 2.1 : 1 that label is a
 * shape rather than a word. Shared so the page and any future PDF renderer
 * agree on what "text-safe" means.
 */
export function contrastOnWhite(hex) {
    if (!/^#?[0-9A-Fa-f]{6}$/.test(hex))
        return 1;
    const value = hex.startsWith('#') ? hex : `#${hex}`;
    /*
     * One luminance implementation in the repo.
     *
     * This had its own copy of the WCAG formula, which is one more place for the
     * sRGB threshold to be written down differently from `brand-palette.ts`'s —
     * and the proposal page's whole contrast guarantee rests on that number. The
     * floor stays, because it is this function's own contract: a contrast claim
     * printed beside a colour picker should never be generous, so 4.49 reads as
     * 4.4 and not as "4.5, passes". The default green is 5.05 and prints as 5.
     * (The comment that used to sit here said it printed as 4.9, which this
     * function has never done — the arithmetic is unchanged, only the claim.)
     */
    return Math.floor(contrast(value, '#ffffff') * 10) / 10;
}
//# sourceMappingURL=organization.js.map